import {Injectable} from "@angular/core";
import {FormArray, FormControl, FormGroup} from "@angular/forms";
import {NavigationEnd, Router} from "@angular/router";
import {BehaviorSubject, Subject} from "rxjs";
import {filter, take} from "rxjs/operators";
import {MoodTest} from "../features/diary/mood-form/mood-form.component";
import {AsthmaTest} from "../models/asthma-test.model";
import {Prescription} from "../features/prescriptions/prescription.model";
import {AppMessage} from "../models/app-message.model";
import {Capture} from "../models/capture";

@Injectable({
  providedIn: "root",
})
export class PatchFormService {
  currentCapture = new Capture();
  saveValues: Subject<any> = new Subject<any>();
  private form = new BehaviorSubject<FormGroup>(null);
  isSubmit$ = this.form.asObservable();
  private formValues: {} = null;
  private editMode = false;
  private backMode = false;
  private isFormSaveBlocked = false;
  private chosenPrescriptions = new BehaviorSubject<Prescription[]>(null);
  private previousUrl: string;
  private currentUrl: string;
  private asthmaTest: AsthmaTest;
  private moodTest: MoodTest;
  private masterMessage: AppMessage;
  private captures: Capture[] = [];
  private fileAttachMode = false;

  constructor(private router: Router) {
    this.currentUrl = this.router.url;
    router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.previousUrl = this.currentUrl;
        this.currentUrl = event.url;
      }
    });
  }

  getForm() {
    // Retrieve the serialized form data from Session Storage
    const formDataString = sessionStorage.getItem("PATCH_FORM");
    if (formDataString) {
      // Parse the serialized data back to a JavaScript object
      const formDataDeserialized = JSON.parse(formDataString);
      // Convert the JavaScript object back to a FormGroup
      const restoredFormData = this.deserializeFormGroup(formDataDeserialized);
      // Use the restored FormGroup
      this.form.next(restoredFormData);
      return restoredFormData;
    }
  }

  saveForm(formData: FormGroup) {
    // Convert the FormGroup to a JSON-serializable format
    const formDataSerializable = this.serializeFormGroup(formData);
    // Save the serialized form data to Session Storage
    sessionStorage.setItem("PATCH_FORM", JSON.stringify(formDataSerializable));
    this.form.next(formData);
  }

  clearForm() {
    sessionStorage.removeItem("PATCH_FORM");
    this.setPhotoCaptures(null);
  }

  getFormValues() {
    const value = sessionStorage.getItem("PATCH_FORM_VALUES");
    this.formValues = JSON.parse(value);
    return this.formValues;
  }

  setFormValues(value: {}) {
    sessionStorage.setItem("PATCH_FORM_VALUES", JSON.stringify(value));
    this.formValues = value;
  }

  saveChosenPrescriptions(prescriptions: Prescription[]) {
    this.chosenPrescriptions.next(prescriptions);
  }

  sendAsthmaTestToReview(test: AsthmaTest) {
    this.asthmaTest = test;
  }

  sendMoodTestToReview(test: MoodTest) {
    this.moodTest = test;
  }

  getAsthmaTest() {
    return this.asthmaTest;
  }

  getMoodTest() {
    return this.moodTest;
  }

  getMasterMessage() {
    return this.masterMessage;
  }

  setMasterMessage(msg: AppMessage) {
    this.masterMessage = msg;
  }

  setEditMode(isEditMode: boolean) {
    this.editMode = isEditMode;
  }

  getEditMode() {
    return this.editMode;
  }

  setBackMode(isBackMode: boolean) {
    this.backMode = isBackMode;
  }

  getBackMode() {
    return this.backMode;
  }

  setFormSaveBlocked(isFormSaveBlocked: boolean) {
    this.isFormSaveBlocked = isFormSaveBlocked;
  }

  getFormSaveBlocked() {
    return this.isFormSaveBlocked;
  }

  setPhotoCaptures(captures: Capture[]) {
    this.captures = captures;
  }

  getPhotoCaptures(): Capture[] {
    return this.captures;
  }

  setFileURL(file: string | ArrayBuffer) {
    this.currentCapture.src = file.toString();
  }

  getFileURL() {
    return this.currentCapture.src;
  }

  setFileName(fileName: string) {
    this.currentCapture.filename = fileName;
  }

  getFileName() {
    return this.currentCapture.filename;
  }

  setRouteLeaveSub(urlsList: string[]) {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd), take(1))
      .subscribe((event: NavigationEnd) => {
        if (urlsList.includes(event.url)) {
          this.saveValues.next(null);
        }
      });
  }

  setFileAttachMode(isFileAttachMode: boolean) {
    this.fileAttachMode = isFileAttachMode;
  }

  getFileAttachMode() {
    return this.fileAttachMode;
  }

  // Function to serialize the FormGroup to a JSON-serializable format
  private serializeFormGroup(formData: FormGroup): any {
    const formControlsData = {};
    Object.keys(formData.controls).forEach(controlName => {
      const control = formData.controls[controlName];
      if (control instanceof FormGroup) {
        formControlsData[controlName] = this.serializeFormGroup(control); // Recursively serialize nested FormGroup
      } else {
        formControlsData[controlName] = control.value;
        if (controlName === "captures" && Array.isArray(control.value)) {
          formControlsData[controlName] = this.serializeCaptures(control.value);
        }
      }
    });
    return {
      controlsData: formControlsData,
      // Include any other necessary metadata if needed
    };
  }

/**
 * Serializes the captures array by retaining essential fields (filename, fileSize, isImage)
 * and setting the `src` property to null to reduce data size during session storage.
 */
  private serializeCaptures(captures: any[]): any[] {
    return captures.map(({ filename, fileSize, isImage }) => ({
      filename,
      fileSize,
      isImage,
      src: null,
    }));
  }

  /**
   * Deserialize the provided data object into a FormGroup with nested FormControls and FormArrays.
   */
  private deserializeFormGroup(data: any): FormGroup {
    const formControls = {};

    Object.keys(data.controlsData).forEach(controlName => {
      const controlData = data.controlsData[controlName];
      if (controlData instanceof Object && !Array.isArray(controlData) && ("controlsData" in controlData)) {
        formControls[controlName] = this.deserializeFormGroup(controlData); // Recursively deserialize nested FormGroup
        // If 'controlData' is an Array, it's a FormArray in our original form
      } else if (Array.isArray(controlData)) {
        // Create a new FormArray and fill it with FormControls created from items of our array
        const formArray = new FormArray([]);
        controlData.forEach(item => formArray.push(new FormControl(item)));
        formControls[controlName] = formArray;
      } else {
        formControls[controlName] = new FormControl(controlData);
      }
    });

    return new FormGroup(formControls);
  }

  /**
   * Dynamically updates a FormArray located at the specified path within a FormGroup
   * by replacing its existing values with the provided new values.
   * Includes error handling and validation.
   */
  updateFormArray(formGroup: FormGroup, path: string, newValues: Capture[]): void {
    const formArray = formGroup.get(path) as FormArray;

    if (!formArray || !(formArray instanceof FormArray)) {
      console.error(`No FormArray found at path '${path}'.`);
      return;
    }

    formArray.clear();

    newValues.forEach((value) => {
      formArray.push(new FormControl(value));
    });

    formArray.updateValueAndValidity();
  }
}

